import React from "react";
import { useParams, Link } from "react-router-dom";
import ServiceDetailTemplate from "../../components/ServiceDetailTemplate";

const serviceData = {
  "b2b-email-lists": {
    title: "B2B Email Lists",
    subtitle: "High-quality, verified B2B contact lists connecting you to global decision-makers.",
    features: [
      "95%+ accuracy guarantee",
      "C-level & director-level targeting",
      "Firmographic & demographic filters",
      "GDPR & CAN-SPAM Compliant",
      "Delivered in CSV, XLSX, or CRM-ready formats",
    ],
    description: `
      Our B2B lists are built from verified, multi-source datasets with
      human validation. Every record goes through manual checks, enrichment,
      and deliverability scoring to ensure maximum campaign performance.
    `,
  },

  "b2c-lead-generation": {
    title: "B2C Lead Generation",
    subtitle: "Reach consumers based on demographics, buying behavior, and intent signals.",
    features: [
      "Demographic filters",
      "Geo & interest-based targeting",
      "Opt-in consumer records",
      "Behavioral analytics",
      "Real-time validation",
    ],
    description: `
      We generate consumer leads through compliant, intent-driven channels.
      Every lead is validated with mobile/email verification to ensure responsiveness.
    `,
  },

  "data-appending-enrichment": {
    title: "Data Appending & Enrichment",
    subtitle: "Fill missing fields and enrich customer records with accurate intelligence.",
    features: [
      "Email & phone appending",
      "Job title & company enrichment",
      "Domain & firmographic signals",
      "Human verification",
      "CRM-ready formatted output",
    ],
    description: `
      Our enrichment process updates your incomplete records with verified
      contact and company details, improving segmentation and campaign accuracy.
    `,
  },

  "list-cleaning-validation": {
    title: "List Cleaning & Email Validation",
    subtitle: "Improve deliverability by removing invalid, duplicate, and risky email addresses.",
    features: [
      "Spam trap removal",
      "MX & SMTP validation",
      "Bounce risk detection",
      "Duplicate cleanup",
      "Detailed validation report",
    ],
    description: `
      AQUIRAA uses multi-step validation to clean your mailing lists,
      protecting your sender reputation and boosting inbox placement.
    `,
  },

  "web-design-development": {
    title: "Web Design & Development",
    subtitle: "Modern, conversion-focused websites designed to generate leads and build credibility.",
    features: [
      "Fully responsive",
      "SEO-optimized structure",
      "High-conversion UI/UX",
      "CRM & form integration",
      "Lightning-fast performance",
    ],
    description: `
      We design polished, high-performance websites that match your brand
      identity and convert visitors into leads using strategic layouts and UX principles.
    `,
  },

  "crm-data-enhancement": {
    title: "CRM Data Enhancement",
    subtitle: "Clean, enrich, and standardize CRM data for smoother workflows and better targeting.",
    features: [
      "Field normalization",
      "De-duplication",
      "Segmentation enhancement",
      "Lead scoring support",
      "Import-ready format delivery",
    ],
    description: `
      We organize, clean, and enrich CRM records so your marketing & sales
      teams can execute campaigns with maximum accuracy.
    `,
  },

  "custom-industry-lists": {
    title: "Custom Industry Lists",
    subtitle: "Hand-curated lists tailored to your industry, audience, and targeting criteria.",
    features: [
      "Healthcare, IT, Manufacturing & more",
      "Revenue, size & role filters",
      "Decision-maker contacts",
      "Geo & vertical targeting",
      "Sample data included",
    ],
    description: `
      Based on your exact requirements, AQUIRAA builds targeted datasets for
      niche segments and industries.
    `,
  },

  "global-database-access": {
    title: "Global Database Access",
    subtitle: "Access compliant, verified business data across 50+ countries.",
    features: [
      "North America, Europe, APAC, MEA",
      "Local compliance checks",
      "Multi-language support",
      "Role & department segmentation",
      "Global accuracy scoring",
    ],
    description: `
      Our global database covers 50+ countries with localized verification
      for accurate, compliant international outreach.
    `,
  },
};

export default function ServiceDynamicPage() {
  const { slug } = useParams();
  const service = serviceData[slug];

  if (!service) {
    return (
      <div className="text-center py-20 text-xl">
        Service not found. <Link to="/services" className="text-blue-600 underline">Go back</Link>
      </div>
    );
  }

  return (
    <ServiceDetailTemplate
      title={service.title}
      subtitle={service.subtitle}
      features={service.features}
    >
      <p className="mt-4 text-gray-700 leading-7 whitespace-pre-line">
        {service.description}
      </p>

      {/* ACTIVE LINK HIGHLIGHT */}
      <div className="mt-6">
        <Link
          to="/services"
          className="text-[#007BFF] underline hover:text-[#005FCC] transition"
        >
          ← Back to All Services
        </Link>
      </div>
    </ServiceDetailTemplate>
  );
}
